<template>
  <div :ref="(el) => { this.captchaRef = el }" style="width:304px;height:78px;"></div>
</template>

<script>
const SITE_KEY = process.env.MIX_RECAPTCHA_SITEKEY

export default {
  name: 'reCAPTCHA',
  props: {
    sitekey: {
      type: String,
      default: SITE_KEY
    }
  },
  data: () => ({
    /** @type {boolean} */
    isReady: false,
    /** @type {string | null} */
    token: null,
    /** @type {HTMLDivElement} */
    captchaRef: null,
    /** @type {number | null} */
    widgetId: null,
    /** @type {number | null} */
    renderInterval: null
  }),
  computed: {
    hasToken() {
      return this.token !== null
    }
  },
  watch: {
    hasToken() {
      this.$emit('verification-changed', this.token)
    }
  },
  methods: {
    /**
     * Verify the reCAPTCHA token with the server.
     * @param token {string} The reCAPTCHA token to verify.
     */
    async verifyToken(token) {
      const verifyUrl = new URL('/verify-captcha', window.location.origin)

      try {
        const request = await fetch(verifyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        })

        if (!request.ok) {
          console.error('Failed to verify reCAPTCHA token:', request.statusText)
          return
        }

        const response = await request.json()
        if (response.success) {
          this.token = token
        }
      } catch (err) {
        console.error('Error verifying reCAPTCHA token:', err)
      }
    },

    /**
     * Handle the reCAPTCHA script load event.
     * - Initialize the reCAPTCHA widget and set the widget ID.
     * - Emit events when the token changes, expires, or an error occurs.
     */
    async renderCaptcha() {
      // Check if grecaptcha is available and the captchaRef is set
      if (this.widgetId !== null || !this.captchaRef) return

      // Wait for the DOM to update before rendering
      await this.$nextTick()

      this.widgetId = window.grecaptcha.render(this.captchaRef, {
        sitekey: this.sitekey,
        callback: this.verifyToken,
        'expired-callback': () => {
          this.token = null
          this.$emit('token-expired')
        },
        'error-callback': () => {
          this.token = null
          this.$emit('token-error')
        }
      })

      this.isReady = true
      this.$emit('recaptcha-ready', this.widgetId)
    }
  },
  mounted() {
    // Poll every 300ms until grecaptcha and captchaRef are ready
    this.renderInterval = setInterval(async () => {
      if (
        window.grecaptcha &&
        typeof window.grecaptcha.render === 'function' &&
        this.captchaRef
      ) {
        clearInterval(this.renderInterval)
        await this.renderCaptcha()
      }
    }, 300)

    // Load reCAPTCHA script if not already loaded
    if (!document.getElementById('grecaptcha-script')) {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
      script.id = 'grecaptcha-script'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
    }
  },
  destroyed() {
    // Clear the interval if it exists
    if (this.renderInterval) {
      clearInterval(this.renderInterval)
    }

    // Reset the reCAPTCHA widget if it exists
    if (this.widgetId !== null && window.grecaptcha) {
      window.grecaptcha.reset(this.widgetId)
    }

    this.widgetId = null
    this.token = null
  }
}
</script>
